<template>
    <div>
        <p v-if="loading">Loading...</p>
        <div v-else>
            <!-- Draggable List -->
            <div ref="sortableList" class="row justify-content-center">
                <div
                    v-for="(quickClass, index) in quickClasses"
                    :key="quickClass.id"
                    class="col-12"
                >
                    <!-- Wrap the component to include the Dropdown button -->
                    <div class="position-relative">
                        <!-- Use the existing QuickClassComponent -->
                        <quick-class-component
                            :quick-class-data="quickClass"
                            :stacked="true"
                            :horizontal="true"
                            :has-content-menu="true"
                            :draggable="true"
                        />
                        <!-- Custom Dropdown Button -->
                        <card-context-menu>
                            <a
                                class="dropdown-item"
                                href="#"
                                @click.prevent="removeQuickClass(quickClass)"
                            >
                                Remove
                            </a>
                        </card-context-menu>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import Sortable from 'sortablejs';
import CardContextMenu from './CardContextMenu.vue';

export default {
    components: {
        CardContextMenu,
    },

    props: {
        playlistId: {
            type: Number,
            default: null,
        },
    },

    data() {
        return {
            playlist: {},
            quickClasses: [],
            loading: true,
            openDropdowns: {}, // Track open dropdowns by quickClass ID
            sortable: null
        };
    },

    mounted() {
        this.fetchPlaylist();
        document.addEventListener('click', this.handleClickOutside);
        this.$nextTick(() => {
            this.initSortable();
        });
    },

    beforeDestroy() {
        document.removeEventListener('click', this.handleClickOutside);
        if (this.sortable) {
            this.sortable.destroy();
        }
    },

    methods: {
        async fetchPlaylist() {
            try {
                const response = await axios.get(
                    `/api/me/playlists/${this.playlistId}?include=quickClasses`
                );
                this.playlist = response.data;
                this.quickClasses = this.playlist.quick_classes;
            } catch (error) {
                console.error('Error fetching playlist:', error);
                alert('Failed to load playlist.');
            } finally {
                this.loading = false;
            }
        },
        initSortable() {
            const el = this.$refs.sortableList;
            if (el) {
                this.sortable = Sortable.create(el, {
                    animation: 200,
                    handle: '.drag-handle',
                    onEnd: (evt) => {
                        // Update the local array
                        const itemEl = evt.item;
                        const newIndex = evt.newIndex;
                        const oldIndex = evt.oldIndex;
                        
                        // Update the quickClasses array
                        const item = this.quickClasses.splice(oldIndex, 1)[0];
                        this.quickClasses.splice(newIndex, 0, item);
                        
                        // Call the backend update
                        this.onDragEnd();
                    }
                });
            }
        },
        async onDragEnd() {
            // Update the order on the backend
            const orderData = this.quickClasses.map((qc, index) => ({
                quick_class_id: qc.id,
                sort_order: index + 1, // Assuming sort order starts from 1
            }));

            try {
                await axios.post(
                    `/api/me/playlists/${this.playlist.id}/reorder?type=quickclasses`,
                    {
                        order: orderData,
                    }
                );
                // Optionally, show a success message
            } catch (error) {
                console.error('Error updating order:', error);
                alert('Failed to update order.');
                // Optionally, reload the playlist to reset the order
                this.fetchPlaylist();
            }
        },
        async removeQuickClass(quickClass) {
            if (
                confirm(
                    `Are you sure you want to remove "${quickClass.name}" from this playlist?`
                )
            ) {
                try {
                    await axios.post(
                        `/api/me/playlists/${this.playlist.id}/remove?type=quickclasses`,
                        {
                            entity_id: quickClass.id,
                        }
                    );
                    // Remove the quick class from the local array
                    this.quickClasses = this.quickClasses.filter(
                        (qc) => qc.id !== quickClass.id
                    );
                    // Optionally, show a success message
                } catch (error) {
                    console.error('Error removing quick class:', error);
                    alert('Failed to remove quick class from playlist.');
                }
            }
        },
        toggleDropdown(id) {
            // Toggle the dropdown open state for the given quickClass ID
            this.$set(this.openDropdowns, id, !this.openDropdowns[id]);
        },
        isDropdownOpen(id) {
            return this.openDropdowns[id];
        },
        handleClickOutside(event) {
            // Close all dropdowns if the click is outside any dropdown
            const dropdowns = this.$el.querySelectorAll('.dropdown');
            let clickedInside = false;

            dropdowns.forEach((dropdown) => {
                if (dropdown.contains(event.target)) {
                    clickedInside = true;
                }
            });

            if (!clickedInside) {
                this.openDropdowns = {};
            }
        },
    },
};
</script>
